import type { ReactNode } from "react";

import { ArrowUpRight } from "@/components/ui/Button";

/**
 * A link that leaves the site, and says so.
 *
 * `rel="noopener noreferrer"` on every one, `target="_blank"`, an outbound
 * arrow, and a screen-reader suffix naming where it goes. The visible host name
 * is not decoration either: the Charity Trek button opens a page that Mountain
 * Routes operates and HISCUS does not, and a visitor should know that before
 * they click rather than after they arrive somewhere unexpected.
 */
export function ExternalLink({
  href,
  children,
  className = "",
  variant = "button",
  showHost = true,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  variant?: "button" | "buttonLight" | "text";
  showHost?: boolean;
}) {
  let host = "";
  try {
    host = new URL(href).hostname.replace(/^www\./, "");
  } catch {
    host = "";
  }

  const base =
    "group inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-[0.9375rem] font-semibold transition-[background-color,border-color,color,gap] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:gap-3";

  const classes =
    variant === "text"
      ? `inline-flex min-h-11 items-center gap-2 text-[0.9375rem] font-semibold text-navy underline underline-offset-4 ${className}`
      : variant === "buttonLight"
        ? `${base} bg-white text-midnight hover:bg-white/92 ${className}`
        : `${base} bg-navy text-white hover:bg-midnight ${className}`;

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={classes}>
      {children}
      <ArrowUpRight className="transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      <span className="sr-only">
        {host && showHost ? ` (opens ${host} in a new tab)` : " (opens in a new tab)"}
      </span>
    </a>
  );
}
